import * as React from 'react';
import styled from 'styled-components';
import Toast from '../../components/toast/Toast';
import {
  ActionButton,
  DescriptionSubtitle,
  DescriptionText,
  DescriptionTitle,
  DisabledText,
  FormAction,
  FormTableHorizontalRows,
} from '../../components/onlineForm/onlineForm';
import { Ikeys, Environments, EnvironmentStatus } from '../../models/developerPortal';
import unreveal from '../../assets/unreveal.svg';
import reveal from '../../assets/reveal.svg';
import Title from '../../components/onlineTitle/Title';
import { texts } from './consts';
import { useCallback, useMemo } from 'react';
import Loader from '../../components/loader/Loader';

const STableLine = styled.div(
  () => `
    display: flex;
    align-items: center;
    overflow: hidden;
    
    & button {
        cursor: pointer;
        margin-left: 20px;
        background: white;
        border: 1px solid black;
        padding: 3px 10px;
        border-radius: 5px;
        font-weight: medium;
    }

    .copy-button {
        padding: 5px 10px;
    }

    .reveal-container {
        justify-content: center;
        display: flex;
        cursor: pointer;
        padding: 5px 5px;
    }
`
);

const StyledDescriptionText = styled(DescriptionText)`
  margin-bottom: 30px;
`;

const SVisibility = styled.img(
  () => `
    width: 20px;
`
);

const SFormTable = styled(FormTableHorizontalRows)`
  max-width: 740px;
  height: 150px;
  margin-bottom: 30px;
  align-items: center;
  display: flex;

  .table-content {
    overflow: hidden;
  }

  .vertical.th {
  }

  .tr {
    overflow: hidden;
  }

  .td {
    overflow: hidden;
  }

  .tr:not(:last-child) {
    margin-bottom: 10px;
  }

  .sunbit-key {
    font-size: 16px;
    overflow: hidden;
    display: inline-block;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  .secret-key {
    color: ${({ theme }) => theme.colors.mediumGray};
    margin-right: 5px;
    font-size: 16px;
    display: inline-block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    flex: 1 1 0;
  }

  .toast {
    display: flex;
    overflow: hidden;
  }

  &.prod-keys {
    background-color: #faf8f6;
  }
`;

export interface IKeysProps {
  onRequestProdAccess?: () => void;
  userKeys: Array<Ikeys>;
  prodRequestStatus: EnvironmentStatus;
  environmentsWaiting: boolean;
  waiting: boolean;
}

export interface IKeysBoxProps {
  keys: Array<Ikeys>;
  keysTitleType: string;
  description?: string;
  isProdKeys?: boolean;
}

const toPrivate = (str: string) => str.replace(/^.{16}/g, '*'.repeat(19));

const KeysBox: React.FC<IKeysBoxProps> = ({ keys = [], keysTitleType, description, isProdKeys }) => {
  const [visibleKey, setToggleVisibleKey] = React.useState(false);
  const { sunbitKey, secretKey } = keys[0] || {};

  const onCopySunbitKey = useCallback(() => navigator.clipboard.writeText(sunbitKey), []);
  const onCopySecretKey = useCallback(() => navigator.clipboard.writeText(secretKey), []);
  const toggleVisibleKey = useCallback(() => setToggleVisibleKey((s) => !s), []);

  return (
    <>
      <DescriptionTitle>{keysTitleType} Keys</DescriptionTitle>
      {description && <StyledDescriptionText>{description}</StyledDescriptionText>}
      <SFormTable className={isProdKeys ? 'prod-keys' : ''}>
        <div className="table-content" key="key-data">
          <div className="tr">
            <div className="th vertical">{texts.SUNBIT_KEY_LABEL}</div>
            <div className="td">
              <Toast
                {...{
                  className: 'toast',
                  text: texts.COPY_SUCCESS_MESSAGE,
                  callback: onCopySunbitKey,
                }}
              >
                <p className="sunbit-key">{sunbitKey}</p>
              </Toast>
            </div>
          </div>
          <div className="tr">
            <div className="th vertical">{texts.SECRET_KEY_LABEL}</div>
            <div className="td">
              <STableLine>
                <p className="secret-key">{visibleKey ? secretKey : toPrivate(secretKey)}</p>
                {secretKey && (
                  <>
                    <div className="reveal-container" onClick={toggleVisibleKey}>
                      <SVisibility src={visibleKey ? unreveal : reveal} />
                    </div>
                    <Toast
                      {...{
                        text: texts.COPY_SUCCESS_MESSAGE,
                        callback: onCopySecretKey,
                      }}
                    >
                      <FormAction className="copy-button">{texts.COPY_ACTION_TEXT}</FormAction>
                    </Toast>
                  </>
                )}
              </STableLine>
            </div>
          </div>
        </div>
      </SFormTable>
    </>
  );
};

const Keys: React.FC<IKeysProps> = ({
  onRequestProdAccess,
  userKeys,
  prodRequestStatus,
  environmentsWaiting,
  waiting,
}) => {
  // Separating envs keys
  const reduceInitials: { sandboxKeys: Array<Ikeys>; prodKeys: Array<Ikeys> } = { sandboxKeys: [], prodKeys: [] };
  const { sandboxKeys, prodKeys } = useMemo(
    () =>
      userKeys.reduce((acc, k) => {
        if (k.environment === Environments.prod) acc.prodKeys.push(k);
        else acc.sandboxKeys.push(k);

        return acc;
      }, reduceInitials),
    [userKeys]
  );

  const prodIsEnabled = EnvironmentStatus.ENABLED === prodRequestStatus;
  const prodIsInProgress = prodRequestStatus == EnvironmentStatus.REQUEST_PENDING;

  return (
    <>
      {waiting && <Loader active />}
      <StyledDescriptionText>
        <Title alignLeft={true}>Keys</Title>
        {texts.KEYS_DESCRIPTION}
      </StyledDescriptionText>

      {!!sandboxKeys?.length && (
        <KeysBox
          {...{
            keys: sandboxKeys,
            keysTitleType: texts.SANDBOX_TITLE_PREFIX,
          }}
        />
      )}
      {!!prodKeys?.length && <KeysBox {...{ keys: prodKeys, keysTitleType: texts.PROD_TITLE_PREFIX }} isProdKeys />}
      {!prodIsEnabled && (
        <ActionButton
          {...{
            dataAt: 'sign-in-submit',
            buttonText: !prodIsInProgress ? texts.REQUEST_PROD_ACCESS_BUTTON : texts.REQUEST_PROD_IN_PROGRESS_BUTTON,
            submit: onRequestProdAccess,
            disabled: prodIsInProgress || environmentsWaiting,
          }}
        />
      )}

      {(prodIsInProgress || environmentsWaiting) && <DisabledText>{texts.PROD_STATUS_PENDING}</DisabledText>}
    </>
  );
};

export default Keys;
